import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, Link2, Inbox, TrendingUp, Calendar, Search, Loader2, Check, CheckCircle2, AlertTriangle, DollarSign, MessageSquare, Flame, Zap, Repeat, Eye, Archive, RotateCcw, ExternalLink, Share2, Pencil } from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { StatusBadge } from "../components/app/StatusBadge";
import { LeadScoreBadge } from "../components/app/LeadScoreBadge";
import { RebookPreviewDialog } from "../components/app/RebookPreviewDialog";
import { AnnouncementBanner } from "../components/app/AnnouncementBanner";
import { BusinessInsights } from "../components/app/BusinessInsights";
import { TodayJobsWidget } from "../components/app/TodayJobsWidget";
import { BookingQrDialog } from "../components/app/BookingQrDialog";
import { BookingPageAnalytics } from "../components/app/BookingPageAnalytics";
import { MarketplaceCard } from "../components/app/MarketplaceCard";
import { isMissedLead, countMissedLeads } from "../lib/leadAlerts";
import { summarizeEarnings, summarizeLeadScores, summarizeConversion, formatCurrency } from "../lib/estimateEarnings";
import { trialDaysLeft, isTrialActive, isTrialExpired } from "../lib/trialStatus";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";

const STATUSES = ["all", "new", "contacted", "quoted", "booked", "closed"];
const SERVICE_TYPES = ["Standard Clean", "Deep Clean", "Move-Out", "Move-In", "Post-Construction", "Airbnb Turnover", "Office / Commercial"];

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [openNew, setOpenNew] = useState(false);
  // Snapshot of the ACTIVE (non-archived) leads list, used only by the
  // onboarding checklist so switching to the Archived tab never regresses
  // milestone progress.
  const [onbLeads, setOnbLeads] = useState([]);
  // Lifetime, archived-inclusive milestone signals from the existing
  // business-insights aggregate (its $match has no archived filter), so
  // archiving the only qualifying lead never regresses checklist progress.
  // Failures fall back to the active-list-only logic.
  const [onbLifetime, setOnbLifetime] = useState(null);
  useEffect(() => {
    api
      .get("/dashboard/business-insights")
      .then(({ data }) =>
        setOnbLifetime({
          quoted: data?.pipeline?.quoted_jobs_lifetime || 0,
          booked: data?.pipeline?.booked_jobs_lifetime || 0,
          closed: data?.pipeline?.closed_jobs_lifetime || 0,
          totalLeads: data?.pipeline?.total_leads_lifetime || 0,
          last30Leads: data?.last_30_days?.leads_created || 0,
        })
      )
      .catch(() => setOnbLifetime(null));
  }, []);
  const [copied, setCopied] = useState(false);

  // Rebooking opportunities — past customers ready for another cleaning. The
  // server filters by status + age + cooldown so we just render whatever it
  // hands us. Only fetched once on mount alongside the leads list; the
  // sendOne() call refreshes by removing the lead client-side instead of
  // re-fetching to keep the widget snappy.
  const [rebookCandidates, setRebookCandidates] = useState([]);
  const [rebookLoading, setRebookLoading] = useState(true);
  const [rebookSendingId, setRebookSendingId] = useState(null);
  // Lead-id whose preview dialog is currently open (null = closed). Local
  // state instead of a per-row useState lets us mount one dialog at the
  // page level and just swap the customer payload it renders.
  const [rebookPreviewLeadId, setRebookPreviewLeadId] = useState(null);

  // SMS notification phone — owner-only setting
  const [smsPhone, setSmsPhone] = useState(user?.phone || "");
  const [savingPhone, setSavingPhone] = useState(false);
  useEffect(() => {
    setSmsPhone(user?.phone || "");
  }, [user?.phone]);

  const savePhone = async () => {
    const trimmed = smsPhone.trim();
    setSavingPhone(true);
    try {
      const { data } = await api.patch("/auth/me", { phone: trimmed });
      setUser(data);
      localStorage.setItem("tasklio_user", JSON.stringify(data));
      toast.success(trimmed ? t("dash.smsPhoneSaved") : t("dash.smsPhoneCleared"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dash.smsPhoneSaveError"));
    } finally {
      setSavingPhone(false);
    }
  };

  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    service_type: "", home_size: "", preferred_date: "", message: "",
  });
  const [creating, setCreating] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const publicFormLink = useMemo(() => {
    if (!user) return "";
    return `${window.location.origin}/l/${user.id}`;
  }, [user]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicFormLink);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  // `viewArchived` toggles the Active / Archived tabs. When true we fetch
  // the archived bucket from the API; otherwise the default (active) list.
  // Stats cards always reflect the active list — archived leads stay in
  // analytics endpoints but the dashboard surface is for live work.
  const [viewArchived, setViewArchived] = useState(false);

  // Restore an archived lead. Called from the row-level button on the
  // Archived tab. After success the lead is no longer archived so the
  // simplest "do the right thing" is to refetch the current bucket — the
  // restored lead drops off the archived list automatically.
  const restoreLead = async (leadId, ev) => {
    if (ev) {
      ev.stopPropagation();
      ev.preventDefault();
    }
    try {
      await api.post(`/leads/${leadId}/restore`);
      toast.success(t("lead.restoreSuccess"));
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("lead.restoreError"));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Always fetch the full list — stats cards and global counters rely on
      // the unfiltered data. Table filtering (status + hot + search) is done
      // entirely on the client via `filtered` below.
      const { data } = await api.get(viewArchived ? "/leads?archived=true" : "/leads");
      setLeads(data);
      if (!viewArchived) setOnbLeads(data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewArchived]);

  // Load rebook candidates separately so a slow query doesn't block the lead
  // table. Failures are swallowed (just hide the widget) — the dashboard
  // must keep working even when this side-feature errors.
  const loadRebookCandidates = async () => {
    setRebookLoading(true);
    try {
      const { data } = await api.get("/leads/rebook/candidates");
      setRebookCandidates(Array.isArray(data) ? data : []);
    } catch {
      setRebookCandidates([]);
    } finally {
      setRebookLoading(false);
    }
  };
  useEffect(() => {
    loadRebookCandidates();
  }, []);

  const sendRebookFollowup = async (leadId) => {
    setRebookSendingId(leadId);
    try {
      await api.post(`/leads/${leadId}/rebook-followup`);
      toast.success("Rebook reminder sent");
      // Drop the lead from the list — the cooldown will keep it out of the
      // server response on the next refresh anyway.
      setRebookCandidates((cur) => cur.filter((c) => c.id !== leadId));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the rebook reminder");
    } finally {
      setRebookSendingId(null);
    }
  };

  const createLead = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      await api.post("/leads", payload);
      toast.success("Lead added");
      setOpenNew(false);
      setForm({ name: "", email: "", phone: "", address: "", service_type: "", home_size: "", preferred_date: "", message: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create lead");
    } finally {
      setCreating(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Tab filter is client-side now — `leads` always holds the full list.
    let base;
    if (filter === "hot") {
      base = leads.filter((l) => l && l.lead_score_label === "hot");
    } else if (filter === "warm") {
      base = leads.filter((l) => l && l.lead_score_label === "warm");
    } else if (filter === "cold") {
      // "cold" filter mirrors summarizeLeadScores: explicit cold OR missing/unknown.
      base = leads.filter((l) => {
        const label = String(l?.lead_score_label || "").toLowerCase();
        return label !== "hot" && label !== "warm";
      });
    } else if (filter && filter !== "all") {
      base = leads.filter((l) => l.status === filter);
    } else {
      base = leads;
    }
    if (!q) return base;
    return base.filter((l) =>
      [l.name, l.email, l.phone, l.service_type, l.address]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [leads, query, filter]);

  const stats = useMemo(() => {
    const by = (s) => leads.filter((l) => l.status === s).length;
    return {
      total: leads.length,
      new: by("new"),
      booked: by("booked"),
      thisWeek: leads.filter((l) => {
        const d = new Date(l.created_at);
        const diff = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
        return diff <= 7;
      }).length,
    };
  }, [leads]);

  // Missed leads — across ALL leads (independent of current filter)
  const allLeads = useMemo(() => leads, [leads]);
  const missedCount = useMemo(() => countMissedLeads(allLeads), [allLeads]);

  // Potential earnings — sum of AI-quoted prices on open leads (visual only).
  const earnings = useMemo(() => summarizeEarnings(leads), [leads]);
  const hasBreakdown =
    earnings.byScore.hot > 0 || earnings.byScore.warm > 0 || earnings.byScore.cold > 0;
  // Count of HOT open leads contributing to earnings — used for the action-oriented subtext.
  const hotLeadsCount = useMemo(
    () =>
      leads.filter(
        (l) =>
          l &&
          l.lead_score_label === "hot" &&
          ["new", "contacted", "quoted"].includes(l.status)
      ).length,
    [leads]
  );

  // Hot / Warm / Cold counts across ALL leads (sales-focused card).
  const scoreCounts = useMemo(() => summarizeLeadScores(leads), [leads]);
  // Funnel progress counts (Total → Contacted → Quoted → Booked).
  const conversion = useMemo(() => summarizeConversion(leads), [leads]);
  const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);

  const isActive = user?.subscription_status === "active";
  const trialActive = isTrialActive(user);
  const trialExpired = isTrialExpired(user);
  const daysLeft = trialDaysLeft(user);

  // Ref to the leads list section — used by the "Respond now" CTA to smooth-scroll.
  const leadsSectionRef = useRef(null);
  // Deterministic scroll: defer to next frame so the post-filter layout is
  // committed, then offset for the sticky navbar (~80–90 px) so the leads
  // section never lands hidden behind it. Double-rAF covers the case where a
  // filter change triggers an async /leads refetch and the page height
  // changes between clicks.
  const scrollToLeadsSection = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = leadsSectionRef.current;
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: y, behavior: "smooth" });
      });
    });
  };
  const handleRespondNow = () => {
    setFilter("new");
    scrollToLeadsSection();
  };
  // Always scroll AND re-apply the "hot" filter, even when it's already active.
  const handleHotHintClick = () => {
    if (filter === "hot") {
      setFilter("all");
      setTimeout(() => setFilter("hot"), 0);
    } else {
      setFilter("hot");
    }
    scrollToLeadsSection();
  };

  // Generic score-tile handler used by Hot / Warm / Cold tiles inside the
  // Lead temperature card. Mirrors handleHotHintClick's "tap-tap re-apply"
  // pattern so a second click still re-runs the scroll, even when the filter
  // hasn't actually changed.
  const handleScoreFilterClick = (score) => {
    if (filter === score) {
      setFilter("all");
      setTimeout(() => setFilter(score), 0);
    } else {
      setFilter(score);
    }
    scrollToLeadsSection();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100" data-testid="dashboard-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 flex flex-col">
        {/* Header row — visual order 1. The previous "+ Add lead" button
            lived here and unintentionally framed manual entry as the
            PRIMARY workflow. It has been moved to a small, low-priority
            "Manual Lead Entry" card at the bottom of the dashboard so
            customers using the Customer Booking Page remain the dominant
            mental model. */}
        <div className="order-1 flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">{t("nav.dashboard")}</p>
            <h1 className="font-display mt-2 text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
              {t("dash.greeting")} {user?.business_name || user?.email?.split("@")[0]}
            </h1>
            <p className="mt-2 text-gray-600 text-base">
              {t("dash.subtitle")}{" "}
              <a
                href="mailto:support@tasklio.co"
                className="text-green-800 hover:underline font-medium"
                data-testid="dashboard-support-link"
              >
                {t("dash.contactSupport")}
              </a>
              .
            </p>
          </div>
        </div>

        {/* Add Lead dialog — moved out of the header. The trigger now lives
            in the low-priority "Manual Lead Entry" card further down. We
            keep the Dialog mounted at the top of the dashboard scope so
            it can be opened from any contextual button without prop
            drilling, but no visible UI is rendered here. */}
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl" data-testid="manual-lead-dialog-title">
                {t("dash.manualLeadTitle")}
              </DialogTitle>
              <DialogDescription data-testid="manual-lead-dialog-description">
                {t("dash.manualLeadHelper")}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={createLead} className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name *</Label>
                  <Input required value={form.name} onChange={setField("name")} className="mt-1.5" data-testid="new-lead-name" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={setField("phone")} className="mt-1.5" data-testid="new-lead-phone" />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={setField("email")} className="mt-1.5" data-testid="new-lead-email" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={setField("address")} className="mt-1.5" data-testid="new-lead-address" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Service</Label>
                  <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="new-lead-service">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Home size</Label>
                  <Input placeholder="e.g. 3 bed / 2 bath" value={form.home_size} onChange={setField("home_size")} className="mt-1.5" data-testid="new-lead-home-size" />
                </div>
              </div>
              <div>
                <Label>Preferred date</Label>
                <Input type="date" value={form.preferred_date} onChange={setField("preferred_date")} className="mt-1.5" data-testid="new-lead-date" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea rows={3} value={form.message} onChange={setField("message")} className="mt-1.5" data-testid="new-lead-message" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={creating} className="bg-green-800 hover:bg-green-900 text-white" data-testid="new-lead-submit">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save lead"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Announcement banner (admin-managed) — order 7. Pushed below the
            primary onboarding flow because it's rarely active; when shown,
            it sits above the lower dashboard panels but never competes
            with the Customer Booking Page card. */}
        <div className="order-7">
          <AnnouncementBanner />
        </div>

        {/* Today's Jobs — order 4. Sits between Leads Waiting (order-3)
            and the KPI stats grid (order-5) per onboarding spec. */}
        <div className="order-4 mt-4">
          <TodayJobsWidget />
        </div>

        {/* Business Insights — moved below the lead list per the
            "leads-first" dashboard layout. Visual order 8. */}
        <div className="order-8">
          <BusinessInsights />
        </div>

        {/* Missed lead alert — order 3 (sits just under header so urgent
            leads are immediately visible) */}
        {missedCount > 0 && (
          <div
            className="order-3 mt-6 rounded-2xl bg-white border border-amber-200 border-l-4 border-l-amber-500 shadow-sm p-5 flex items-start gap-3"
            data-testid="missed-leads-banner"
          >
            <span className="w-9 h-9 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-600 alive-icon-pulse" />
            </span>
            <div className="flex-1">
              <p className="font-semibold text-gray-900" data-testid="missed-leads-count-text">
                {missedCount === 1 ? t("dash.missedBannerOne") : t("dash.missedBannerMany", missedCount)}
              </p>
              <p className="text-sm text-amber-700 mt-0.5">
                {t("dash.missedHelper")}
              </p>
            </div>
            <button
              onClick={handleRespondNow}
              className="self-center text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg px-4 py-2 transition-all shadow-sm hover:shadow-md"
              data-testid="view-missed-leads-button"
            >
              {t("dash.respondNow")}
            </button>
          </div>
        )}

        {/* Trial / subscription banner — order 3 (sits with urgent
            banners so subscription status reads alongside missed leads) */}
        {trialActive && (
          <div
            className="order-3 mt-6 rounded-2xl bg-gradient-to-r from-green-50 to-sky-50 border border-green-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="trial-active-banner"
          >
            <div className="flex-1">
              <p className="font-display font-medium text-gray-900" data-testid="trial-active-title">
                {t("dash.trialActiveTitle", daysLeft)}
              </p>
              <p className="text-sm text-gray-600 mt-0.5">
                {t("dash.trialActiveBody")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/pricing#pricing")}
              className="bg-green-800 hover:bg-green-900 text-white"
              data-testid="trial-upgrade-button"
            >
              {t("dash.upgradeNow")}
            </Button>
          </div>
        )}

        {trialExpired && (
          <div
            className="order-3 mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="trial-expired-banner"
          >
            <div className="flex-1">
              <p className="font-display font-medium text-amber-900" data-testid="trial-expired-title">
                {t("dash.trialExpiredTitle")}
              </p>
              <p className="text-sm text-amber-800/80 mt-0.5">
                {t("dash.trialExpiredBody")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/pricing#pricing")}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="trial-expired-upgrade-button"
            >
              {t("dash.trialExpiredCta")}
            </Button>
          </div>
        )}

        {/* Potential Earnings — DARK PREMIUM HERO — order 6 (sits below
            the lead list so revenue motivation reads after the user has
            triaged today's leads) */}
        <div
          className={`order-6 mt-10 relative overflow-hidden rounded-2xl border border-green-700/40 ring-1 ring-green-400/20 bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 shadow-xl p-7 sm:p-8${hotLeadsCount > 0 ? " alive-pulse-glow" : ""}`}
          data-testid="potential-earnings-card"
        >
          {/* soft glow accents */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-300/15 blur-3xl" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-green-400/10 blur-3xl" aria-hidden="true" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <DollarSign className="w-6 h-6 text-green-100" />
              </span>
              <div>
                <p className="text-xs font-semibold text-green-100 uppercase tracking-[0.18em]">{t("dash.potentialRevenue")}</p>
                <p
                  className="mt-1 font-display text-5xl sm:text-6xl font-extrabold text-white tracking-tight"
                  data-testid="potential-earnings-amount"
                >
                  {formatCurrency(earnings.total)}
                </p>
                {earnings.leadCount > 0 ? (
                  <button
                    type="button"
                    onClick={handleHotHintClick}
                    className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-green-100 hover:text-white hover:underline cursor-pointer text-left transition-colors"
                    data-testid="potential-earnings-subtext"
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${hotLeadsCount > 0 ? "bg-green-300 alive-dot shadow-[0_0_0_3px_rgba(74,222,128,0.25)]" : "bg-green-300"}`}
                      aria-hidden="true"
                    />
                    {t("dash.fromActiveLeads", earnings.leadCount)}
                  </button>
                ) : (
                  <p
                    className="mt-2 text-sm text-green-100"
                    data-testid="potential-earnings-subtext"
                  >
                    {t("dash.noActiveLeadsYet")}
                  </p>
                )}
              </div>
            </div>

            {hasBreakdown && (
              <div
                className="grid grid-cols-3 gap-3 w-full sm:w-auto sm:min-w-[340px]"
                data-testid="potential-earnings-breakdown"
              >
                <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-3 backdrop-blur-sm transition-transform hover:scale-[1.02]">
                  <p className="text-[11px] uppercase tracking-wide font-bold text-red-200 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-300" /> {t("dash.scoreHot")}
                  </p>
                  <p className="mt-1 font-display text-base font-bold text-white" data-testid="earnings-hot">
                    {formatCurrency(earnings.byScore.hot)}
                  </p>
                </div>
                <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-3 backdrop-blur-sm transition-transform hover:scale-[1.02]">
                  <p className="text-[11px] uppercase tracking-wide font-bold text-amber-200 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-300" /> {t("dash.scoreWarm")}
                  </p>
                  <p className="mt-1 font-display text-base font-bold text-white" data-testid="earnings-warm">
                    {formatCurrency(earnings.byScore.warm)}
                  </p>
                </div>
                <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-3 backdrop-blur-sm transition-transform hover:scale-[1.02]">
                  <p className="text-[11px] uppercase tracking-wide font-bold text-blue-200 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-300" /> {t("dash.scoreCold")}
                  </p>
                  <p className="mt-1 font-display text-base font-bold text-white" data-testid="earnings-cold">
                    {formatCurrency(earnings.byScore.cold)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats — order 4 (filter buttons that scroll to the lead list
            just below). Sits ABOVE the leads list so the user can jump
            straight to a status bucket. */}
        <div className="order-5 mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: t("dash.statTotalLeads"),
              value: stats.total,
              icon: Inbox,
              targetFilter: "all",
              ariaLabel: "Show all leads",
              testKey: "total-leads",
              cardBg: "bg-gradient-to-br from-green-50 to-emerald-50/60",
              cardBorder: "border-green-200/70",
              cardHoverBorder: "hover:border-green-300",
              ringColor: "focus:ring-green-500",
              chipBg: "bg-green-600",
              numberColor: "text-green-900",
            },
            {
              label: t("dash.statNew"),
              value: stats.new,
              icon: Zap,
              targetFilter: "new",
              ariaLabel: "Show new leads",
              testKey: "new",
              cardBg: "bg-gradient-to-br from-sky-50 to-blue-50/60",
              cardBorder: "border-sky-200/70",
              cardHoverBorder: "hover:border-sky-300",
              ringColor: "focus:ring-sky-500",
              chipBg: "bg-sky-600",
              numberColor: "text-sky-900",
            },
            {
              label: t("dash.statBooked"),
              value: stats.booked,
              icon: Calendar,
              targetFilter: "booked",
              ariaLabel: "Show booked leads",
              testKey: "booked",
              cardBg: "bg-gradient-to-br from-emerald-50 to-teal-50/60",
              cardBorder: "border-emerald-200/70",
              cardHoverBorder: "hover:border-emerald-300",
              ringColor: "focus:ring-emerald-500",
              chipBg: "bg-emerald-600",
              numberColor: "text-emerald-900",
            },
            {
              label: t("dash.statLast7"),
              value: stats.thisWeek,
              icon: TrendingUp,
              targetFilter: "all",
              ariaLabel: "Show leads from last 7 days",
              testKey: "last-7-days",
              cardBg: "bg-gradient-to-br from-amber-50 to-yellow-50/60",
              cardBorder: "border-amber-200/70",
              cardHoverBorder: "hover:border-amber-300",
              ringColor: "focus:ring-amber-500",
              chipBg: "bg-amber-500",
              numberColor: "text-amber-900",
            },
          ].map((s) => (
            <button
              key={s.testKey}
              type="button"
              onClick={() => { setFilter(s.targetFilter); scrollToLeadsSection(); }}
              className={`text-left w-full ${s.cardBg} border ${s.cardBorder} ${s.cardHoverBorder} shadow-sm rounded-2xl p-5 sm:p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 ${s.ringColor} focus:ring-offset-2`}
              data-testid={`stat-${s.testKey}`}
              aria-label={s.ariaLabel}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 tracking-wide">{s.label}</p>
                <span className={`w-9 h-9 rounded-xl ${s.chipBg} text-white flex items-center justify-center shadow-sm shrink-0`}>
                  <s.icon className="w-4 h-4" />
                </span>
              </div>
              <p className={`mt-4 font-display text-4xl sm:text-5xl font-extrabold ${s.numberColor} tracking-tight tabular-nums`}>
                {s.value}
              </p>
            </button>
          ))}
        </div>

        {/* Rebooking Opportunities — re-engage past customers who haven't
            been nudged this month. Styled as a "growth/revenue" surface
            (gradient + emerald accent + iconified header) so it visually
            reads as a value-creating action rather than a passive list.
            Hidden when there are zero candidates so the dashboard stays
            clean. */}
        {!rebookLoading && rebookCandidates.length > 0 && (
          <div
            className="order-9 mt-6 relative overflow-hidden rounded-2xl border-2 border-green-200 bg-gradient-to-br from-emerald-50 via-white to-green-50 shadow-sm hover:shadow-md transition-shadow"
            data-testid="rebook-card"
          >
            {/* Decorative corner glow — purely visual, very subtle. */}
            <div className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full bg-emerald-200/40 blur-3xl" aria-hidden="true" />
            <div className="relative p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-green-700 text-white shadow-sm shrink-0">
                    <Repeat className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold text-green-800 uppercase tracking-[0.18em]">
                      {t("dash.growthOpportunity")}
                    </p>
                    <h3 className="mt-0.5 font-display text-xl font-semibold text-gray-900">
                      {t("dash.rebookingOpportunities")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-700 font-medium" data-testid="rebook-summary">
                      {t("dash.rebookSummary")}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t("dash.rebookSubcopy")}
                    </p>
                  </div>
                </div>
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-white border border-green-200 px-2.5 py-1 text-xs font-semibold text-green-800 shrink-0"
                  data-testid="rebook-count-pill"
                >
                  {rebookCandidates.length} {t("dash.rebookReadyPill")}
                </span>
              </div>
              <ul className="mt-5 divide-y divide-green-100/80" data-testid="rebook-list">
                {rebookCandidates.map((c) => (
                  <li
                    key={c.id}
                    className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4"
                    data-testid={`rebook-row-${c.id}`}
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/leads/${c.id}`}
                        className="font-semibold text-gray-900 hover:text-green-800 truncate block"
                        data-testid={`rebook-name-${c.id}`}
                      >
                        {c.name}
                      </Link>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {c.days_since_booked === 1
                          ? t("dash.rebookOneDay")
                          : t("dash.rebookManyDays", c.days_since_booked)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => sendRebookFollowup(c.id)}
                        disabled={rebookSendingId === c.id}
                        className="h-9 bg-green-700 hover:bg-green-800 text-white shadow-sm"
                        data-testid={`rebook-send-${c.id}`}
                      >
                        {rebookSendingId === c.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Repeat className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        {t("dash.sendFollowUp")}
                      </Button>
                      {/* Preview only — no network call, no send. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRebookPreviewLeadId(c.id)}
                        className="h-9 px-2 text-gray-500 hover:text-gray-800 hover:bg-white/60"
                        aria-label={`Preview rebook reminder for ${c.name}`}
                        title={t("dash.previewEmail")}
                        data-testid={`rebook-preview-${c.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Hot Leads + Lead progress (sales-focused metrics) — order 10 */}
        <div className="order-10 mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Hot leads card — emphasises Hot, shows warm/cold alongside */}
          <div
            className="bg-white border border-gray-100 shadow-sm rounded-2xl p-6"
            data-testid="hot-leads-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.18em]">{t("dash.leadTemperature")}</p>
                <p className="mt-1 text-sm text-gray-500">{t("dash.leadTemperatureHelp")}</p>
              </div>
              <span className="w-9 h-9 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                <Flame className="w-4 h-4 text-red-500" />
              </span>
            </div>

            {/* Hot — emphasised */}
            <button
              type="button"
              onClick={() => handleScoreFilterClick("hot")}
              aria-label="Show hot leads"
              data-testid="hot-leads-tile"
              className="mt-5 w-full text-left rounded-xl bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 p-4 flex items-center justify-between cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.01] hover:border-red-200 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-red-500 text-white flex items-center justify-center shadow-sm">
                  <Flame className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-bold text-red-700">{t("dash.scoreHot")}</p>
                  <p className="text-xs text-red-700/80">{t("dash.hotReady")}</p>
                </div>
              </div>
              <p
                className="font-display text-4xl font-extrabold text-red-600 tracking-tight"
                data-testid="hot-leads-count"
              >
                {scoreCounts.hot}
              </p>
            </button>

            {/* Warm / Cold — smaller */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleScoreFilterClick("warm")}
                aria-label="Show warm leads"
                data-testid="warm-leads-tile"
                className="rounded-xl bg-amber-50 border border-amber-100 p-3 flex items-center justify-between cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] hover:border-amber-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <p className="text-xs font-semibold text-amber-800">{t("dash.scoreWarm")}</p>
                </div>
                <p
                  className="font-display text-lg font-bold text-amber-700"
                  data-testid="warm-leads-count"
                >
                  {scoreCounts.warm}
                </p>
              </button>
              <button
                type="button"
                onClick={() => handleScoreFilterClick("cold")}
                aria-label="Show cold leads"
                data-testid="cold-leads-tile"
                className="rounded-xl bg-sky-50 border border-sky-100 p-3 flex items-center justify-between cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] hover:border-sky-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-sky-400" />
                  <p className="text-xs font-semibold text-sky-800">{t("dash.scoreCold")}</p>
                </div>
                <p
                  className="font-display text-lg font-bold text-sky-700"
                  data-testid="cold-leads-count"
                >
                  {scoreCounts.cold}
                </p>
              </button>
            </div>
          </div>

          {/* Lead progress (conversion funnel) */}
          <div
            className="bg-white border border-gray-100 shadow-sm rounded-2xl p-6"
            data-testid="lead-progress-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.18em]">{t("dash.leadProgress")}</p>
                <p className="mt-1 text-sm text-gray-500">
                  {conversion.total > 0
                    ? t("dash.percentBooked", pct(conversion.booked, conversion.total))
                    : t("dash.leadProgressEmpty")}
                </p>
              </div>
              <span className="w-9 h-9 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
                <TrendingUp className="w-4 h-4 text-green-700" />
              </span>
            </div>

            <div className="mt-5 space-y-3">
              {[
                { key: "total",      label: t("dash.progressTotal"),     value: conversion.total,      color: "bg-gray-900",  testid: "progress-total" },
                { key: "contacted",  label: t("dash.progressContacted"), value: conversion.contacted,  color: "bg-sky-500",   testid: "progress-contacted" },
                { key: "quoted",     label: t("dash.progressQuoted"),    value: conversion.quoted,     color: "bg-amber-500", testid: "progress-quoted" },
                { key: "booked",     label: t("dash.progressBooked"),    value: conversion.booked,     color: "bg-green-600", testid: "progress-booked" },
              ].map((row) => {
                const percent = row.key === "total" ? 100 : pct(row.value, conversion.total);
                return (
                  <div key={row.key}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 font-medium">{row.label}</span>
                      <span className="text-gray-900 font-semibold tabular-nums" data-testid={row.testid}>
                        {row.value}
                        {row.key !== "total" && conversion.total > 0 ? (
                          <span className="ml-2 text-xs font-medium text-gray-500">{percent}%</span>
                        ) : null}
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${row.color} transition-all duration-500`}
                        style={{ width: `${Math.max(percent, conversion.total === 0 ? 0 : 4)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Customer Booking Page — order-2 so it sits directly under the
            dashboard header. This is intentionally THE first thing the
            owner sees after the greeting: it's where every customer-
            submitted lead originates. Replacing the old order-11 "public
            link" footer card AND the in-header "Add lead" button so the
            primary workflow (customers submit through this page) is the
            dominant onboarding signal. */}
        <section
          className="order-2 mt-6 rounded-2xl border-2 border-green-200 bg-gradient-to-br from-emerald-50 via-white to-green-50 shadow-sm hover:shadow-md transition-shadow"
          data-testid="customer-booking-page-card"
        >
          <div className="p-5 sm:p-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              {/* Left: identity + helper copy */}
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <span className="w-11 h-11 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center shrink-0">
                  <Share2 className="w-5 h-5 text-green-800" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold tracking-wider uppercase text-green-800">
                    {t("dash.bookingPageEyebrow")}
                  </p>
                  <h2
                    className="mt-1 font-display text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight"
                    data-testid="customer-booking-page-title"
                  >
                    {t("dash.bookingPageTitle")}
                  </h2>
                  <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">
                    {t("dash.bookingPageDescription")}
                  </p>
                  {/* The page URL itself — truncated, monospaced-ish so
                      owners can quickly verify it's correct before sharing. */}
                  <p
                    className="mt-3 text-xs text-gray-500 truncate font-mono bg-white border border-gray-200 rounded-md px-2.5 py-1.5 inline-block max-w-full"
                    data-testid="customer-booking-page-url"
                  >
                    {publicFormLink}
                  </p>
                </div>
              </div>

              {/* Right: actions. Stack on mobile, inline on desktop. */}
              <div
                className="flex flex-col sm:flex-row gap-2 md:flex-col lg:flex-row md:items-stretch lg:items-center shrink-0"
                data-testid="customer-booking-page-actions"
              >
                <Button
                  onClick={copyLink}
                  className="bg-green-800 hover:bg-green-900 text-white h-11 px-5 font-semibold"
                  data-testid="customer-booking-page-copy"
                >
                  {copied ? (
                    <><Check className="w-4 h-4 mr-1.5" /> {t("dash.bookingPageCopied")}</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-1.5" /> {t("dash.bookingPageCopy")}</>
                  )}
                </Button>
                <Link to={`/l/${user?.id}?preview=1`} target="_blank" rel="noreferrer">
                  <Button
                    variant="outline"
                    className="h-11 px-5 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50 w-full"
                    data-testid="customer-booking-page-open"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" /> {t("dash.bookingPageOpen")}
                  </Button>
                </Link>
                <Link to="/booking-page-settings">
                  <Button
                    variant="outline"
                    className="h-11 px-5 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50 w-full"
                    data-testid="customer-booking-page-customize"
                  >
                    <Pencil className="w-4 h-4 mr-1.5" /> {t("dash.bookingPageCustomize")}
                  </Button>
                </Link>
                <BookingQrDialog link={publicFormLink} businessName={user?.business_name} t={t} />
              </div>
            </div>

            <BookingPageAnalytics t={t} />

            <p className="mt-4 text-xs text-gray-600 leading-relaxed">
              {t("dash.bookingPageHelper")}
            </p>

            {/* Get Your First Booking checklist — progress-aware (Onboarding
                Phase A). Milestones are derived purely from data the
                Dashboard already loads (auth user + active leads list):
                  1. Booking page prepared  → owner saved a tagline, about,
                     logo, or customized services (never "complete by default")
                  2. First lead             → any active lead exists
                  3. First quote sent       → a lead reached quoted/booked/
                     closed (quoted_at is stamped on that transition) —
                     generated-but-unsent drafts intentionally don't count
                  4. First booking          → a lead reached booked/closed
                Once all four are done the list collapses to a one-line
                success state to free Dashboard space. */}
            {(() => {
              const pagePrepared = !!(
                user?.booking_tagline ||
                user?.booking_about ||
                user?.booking_logo_url ||
                (Array.isArray(user?.enabled_services) && user.enabled_services.length > 0)
              );
              const ltQuoted = (onbLifetime?.quoted || 0) + (onbLifetime?.booked || 0) + (onbLifetime?.closed || 0);
              const ltBooked = (onbLifetime?.booked || 0) + (onbLifetime?.closed || 0);
              const firstLead =
                onbLeads.length > 0 ||
                (onbLifetime?.totalLeads || 0) > 0 ||
                ltQuoted > 0 ||
                (onbLifetime?.last30Leads || 0) > 0;
              const firstQuote =
                ltQuoted > 0 ||
                onbLeads.some(
                  (l) => l.quoted_at || ["quoted", "booked", "closed"].includes(l.status)
                );
              const firstBooking =
                ltBooked > 0 || onbLeads.some((l) => ["booked", "closed"].includes(l.status));
              const steps = [
                {
                  key: "page",
                  done: pagePrepared,
                  label: t("dash.onbStep1"),
                  action: (
                    <Link
                      to="/booking-page-settings"
                      className="text-xs font-semibold text-green-800 underline underline-offset-2 hover:text-green-900"
                      data-testid="onb-setup-page-link"
                    >
                      {t("dash.onbStep1Action")}
                    </Link>
                  ),
                },
                {
                  key: "lead",
                  done: firstLead,
                  label: t("dash.onbStep2"),
                  action: (
                    <button
                      type="button"
                      onClick={() => setOpenNew(true)}
                      className="text-xs font-semibold text-green-800 underline underline-offset-2 hover:text-green-900"
                      data-testid="onb-add-lead-btn"
                    >
                      {t("dash.onbStep2Action")}
                    </button>
                  ),
                },
                { key: "quote", done: firstQuote, label: t("dash.onbStep3"), hint: t("dash.onbStep3Hint") },
                { key: "booked", done: firstBooking, label: t("dash.onbStep4"), hint: t("dash.onbStep4Hint") },
              ];
              const doneCount = steps.filter((s) => s.done).length;
              if (doneCount === steps.length) {
                return (
                  <div
                    className="mt-5 pt-5 border-t border-green-200 flex items-center gap-2.5"
                    data-testid="first-booking-complete"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0" />
                    <p className="text-sm text-gray-800">{t("dash.onbAllDone")}</p>
                  </div>
                );
              }
              return (
                <div
                  className="mt-5 pt-5 border-t border-green-200"
                  data-testid="first-booking-checklist"
                >
                  <div className="flex items-center justify-between mb-3 gap-3">
                    <p className="text-xs font-semibold tracking-wider uppercase text-green-800">
                      {t("dash.firstBookingTitle")}
                    </p>
                    <p className="text-xs font-semibold text-green-800" data-testid="first-booking-progress">
                      {t("dash.onbProgress", doneCount)}
                    </p>
                  </div>
                  <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {steps.map((step, idx) => (
                      <li
                        key={step.key}
                        className="flex items-start gap-2.5 text-sm"
                        data-testid={`first-booking-step-${idx + 1}`}
                      >
                        {step.done ? (
                          <span
                            className="shrink-0 w-6 h-6 rounded-full bg-green-700 text-white flex items-center justify-center"
                            data-testid={`first-booking-step-${idx + 1}-done`}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </span>
                        ) : (
                          <span className="shrink-0 w-6 h-6 rounded-full bg-white border border-green-300 text-green-800 text-xs font-semibold flex items-center justify-center">
                            {idx + 1}
                          </span>
                        )}
                        <span className="leading-relaxed min-w-0">
                          <span className={step.done ? "text-gray-500" : "text-gray-800"}>
                            {step.label}
                          </span>
                          {!step.done && step.action ? (
                            <span className="ml-2 whitespace-nowrap">{step.action}</span>
                          ) : null}
                          {!step.done && step.hint ? (
                            <span className="block text-xs text-gray-500 mt-0.5">{step.hint}</span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })()}
          </div>
        </section>

        <MarketplaceCard user={user} t={t} />

        {/* SMS notification phone — order 12 */}
        <div
          className="order-12 mt-4 bg-white border border-gray-100 shadow-sm rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-3"
          data-testid="sms-phone-card"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5 text-green-800" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900">{t("dash.smsPhoneTitle")}</p>
              <p className="text-xs text-gray-500">
                {t("dash.smsPhoneHelp")}
              </p>
            </div>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Input
              type="tel"
              inputMode="tel"
              placeholder="+14049177181"
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.target.value)}
              className="h-10 md:w-56"
              data-testid="sms-phone-input"
            />
            <Button
              onClick={savePhone}
              disabled={savingPhone || (smsPhone.trim() === (user?.phone || ""))}
              className="bg-green-800 hover:bg-green-900 text-white h-10 shrink-0"
              data-testid="sms-phone-save"
            >
              {savingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : t("dash.smsPhoneSave")}
            </Button>
          </div>
        </div>

        {/* Manual Lead Entry — `order-last` so it always sits at the very
            bottom of the dashboard. DELIBERATELY low-priority styling
            (small, neutral gray card, secondary button, helper text) so
            it can never visually compete with the Customer Booking Page
            card at the top. This is the exception flow for phone calls /
            referrals / walk-ins, never the primary entry point. */}
        <section
          className="order-last mt-4 rounded-2xl bg-white border border-gray-200 shadow-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          data-testid="manual-lead-entry-card"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <span
              className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0"
              aria-hidden="true"
            >
              <Plus className="w-5 h-5 text-gray-600" />
            </span>
            <div className="min-w-0 flex-1">
              <p
                className="text-sm font-semibold text-gray-900"
                data-testid="manual-lead-entry-title"
              >
                {t("dash.manualLeadEntryTitle")}
              </p>
              <p
                className="mt-1 text-xs text-gray-500 leading-relaxed"
                data-testid="manual-lead-entry-description"
              >
                {t("dash.manualLeadEntryDescription")}
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setOpenNew(true)}
            variant="outline"
            className="h-10 px-4 border-gray-300 text-gray-800 hover:bg-gray-50 shrink-0"
            data-testid="manual-lead-entry-button"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {t("dash.addLead")}
          </Button>
        </section>

        {/* Leads list — order 5 (primary daily workflow surface, moved
            high so users can triage leads without scrolling past
            analytics) */}
        <div className="order-6 mt-10" ref={leadsSectionRef} data-testid="leads-section">
          {/* Active / Archived tab — switches the bucket the API returns.
              Sits ABOVE the status filter pills so it reads as the top-level
              choice ("what am I looking at?") rather than mixing with the
              within-bucket status filter. */}
          <div className="mb-3 inline-flex items-center rounded-lg bg-gray-100 p-0.5 border border-gray-200">
            <button
              type="button"
              onClick={() => setViewArchived(false)}
              className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${
                !viewArchived
                  ? "bg-white text-green-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="leads-tab-active"
            >
              {t("dash.tabActive")}
            </button>
            <button
              type="button"
              onClick={() => setViewArchived(true)}
              className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${
                viewArchived
                  ? "bg-white text-amber-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="leads-tab-archived"
            >
              {t("dash.tabArchived")}
            </button>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Overflow fix: ES/long labels make this tab row wider than
                small phones — only this row scrolls, never the page. */}
            <div className="max-w-full overflow-x-auto" data-testid="leads-filter-scroll">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="bg-gray-100">
                {STATUSES.map((s) => {
                  const labelKey = {
                    all: "dash.filterAll",
                    new: "dash.filterNew",
                    contacted: "dash.filterContacted",
                    quoted: "dash.filterQuoted",
                    booked: "dash.filterBooked",
                    closed: "dash.filterClosed",
                  }[s];
                  return (
                    <TabsTrigger key={s} value={s} className="data-[state=active]:bg-white data-[state=active]:text-green-800" data-testid={`filter-${s}`}>
                      {t(labelKey)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
            </div>
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t("dash.searchPlaceholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 h-10"
                data-testid="leads-search"
              />
            </div>
          </div>

          <div className="mt-4 bg-white border border-gray-100 shadow-sm rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-12 flex items-center justify-center text-gray-400" data-testid="leads-loading">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center" data-testid="leads-empty">
                <div className="w-14 h-14 rounded-2xl bg-gray-50 mx-auto flex items-center justify-center">
                  <Inbox className="w-6 h-6 text-gray-400" />
                </div>
                <p className="mt-4 font-display text-lg font-medium text-gray-900">
                  {viewArchived ? t("dash.archivedEmpty") : t("dash.noLeadsYet")}
                </p>
                {!viewArchived && (
                  <p className="mt-1 text-sm text-gray-500">{t("dash.noLeadsHint")}</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                      <th className="text-left font-medium py-3 px-6">{t("dash.tableHeaderName")}</th>
                      <th className="text-left font-medium py-3 px-6 hidden md:table-cell">{t("dash.tableHeaderContact")}</th>
                      <th className="text-left font-medium py-3 px-6 hidden lg:table-cell">{t("dash.tableHeaderService")}</th>
                      <th className="text-left font-medium py-3 px-6">{t("dash.tableHeaderStatus")}</th>
                      <th className="text-left font-medium py-3 px-6 hidden sm:table-cell">{t("dash.tableHeaderScore")}</th>
                      <th className="text-left font-medium py-3 px-6 hidden sm:table-cell">{t("dash.tableHeaderAdded")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => {
                      const rowAccent =
                        l.status === "new"
                          ? "border-l-4 border-l-blue-400"
                          : "border-l-4 border-l-transparent";
                      const rowHover =
                        l.lead_score_label === "hot"
                          ? "hover:bg-red-50/60"
                          : "hover:bg-gray-50";
                      return (
                      <tr
                        key={l.id}
                        onClick={() => navigate(`/leads/${l.id}`)}
                        className={`border-t border-gray-100 ${rowAccent} ${rowHover} cursor-pointer transition-colors`}
                        data-testid={`lead-row-${l.id}`}
                      >
                        <td className="py-5 px-6">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900">{l.name}</p>
                            <span className="sm:hidden">
                              <LeadScoreBadge label={l.lead_score_label} />
                            </span>
                            {isMissedLead(l) && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                                data-testid={`missed-badge-${l.id}`}
                                title="Not contacted in 2 hours"
                              >
                                <AlertTriangle className="w-3 h-3" />
                                {t("dash.missedBadge")}
                              </span>
                            )}
                            {l.returning_customer && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-800 border border-green-200"
                                data-testid={`returning-customer-badge-${l.id}`}
                                title="Came back via a Rebook Reminder email"
                              >
                                <Repeat className="w-3 h-3" />
                                {t("dash.returningCustomerBadge")}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 md:hidden">{l.email || l.phone || "—"}</p>
                        </td>
                        <td className="py-5 px-6 hidden md:table-cell text-gray-600">
                          <div className="truncate max-w-[200px]">{l.email || "—"}</div>
                          <div className="text-xs text-gray-500">{l.phone || ""}</div>
                        </td>
                        <td className="py-5 px-6 hidden lg:table-cell text-gray-600">{l.service_type || "—"}</td>
                        <td className="py-5 px-6">
                          <div className="flex items-center gap-2 flex-wrap">
                            <StatusBadge status={l.status} />
                            {l.is_archived && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                                data-testid={`archived-badge-${l.id}`}
                                title="Hidden from active list — still in analytics"
                              >
                                <Archive className="w-3 h-3" />
                                {t("lead.archived")}
                              </span>
                            )}
                          </div>
                          {viewArchived && (
                            <button
                              type="button"
                              onClick={(e) => restoreLead(l.id, e)}
                              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 hover:text-emerald-900 hover:underline"
                              data-testid={`restore-lead-button-${l.id}`}
                            >
                              <RotateCcw className="w-3 h-3" />
                              {t("lead.restore")}
                            </button>
                          )}
                        </td>
                        <td className="py-5 px-6 hidden sm:table-cell">
                          {l.lead_score_label ? (
                            <LeadScoreBadge label={l.lead_score_label} />
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-5 px-6 hidden sm:table-cell text-gray-500">
                          {new Date(l.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      {(() => {
        const previewLead = rebookCandidates.find((c) => c.id === rebookPreviewLeadId);
        // Build the CTA URL the customer will actually see in the email.
        // Format mirrors backend `_build_returning_customer_form_url` so the
        // preview is faithful. Origin defaults to the current page so the
        // owner can click through and verify the prefill works end-to-end.
        let ctaUrl = null;
        if (previewLead && user?.id) {
          const params = new URLSearchParams();
          params.set("returning_customer", "true");
          params.set("previous_lead_id", previewLead.id);
          if (previewLead.name) params.set("customer_name", previewLead.name);
          if (previewLead.email) params.set("customer_email", previewLead.email);
          ctaUrl = `${window.location.origin}/l/${user.id}?${params.toString()}`;
        }
        return (
          <RebookPreviewDialog
            open={Boolean(previewLead)}
            onOpenChange={(o) => { if (!o) setRebookPreviewLeadId(null); }}
            customerName={previewLead?.name}
            user={user}
            ctaUrl={ctaUrl}
            triggerTestId="dashboard-rebook-preview-dialog"
          />
        );
      })()}
    </div>
  );
}
